import { eventQueue } from './EventQueue';
import { playTimeNoteFlushController } from './PlayTimeNoteFlushController';

// The two event senders: the batched event queue and the play-time note flush.

// Page hidden, pagehide or app backgrounded: both send what they hold over
// keepalive. The play-time notes go first, so they claim their share of the page's
// keepalive allowance before the queue takes what is left (see keepaliveBudget).
export const flushEventSendersOnTeardown = (): void => {
  playTimeNoteFlushController.flushOnTeardown().catch(() => {});
  eventQueue.flushOnTeardown().catch(() => {});
};

// The most a sign-out waits for the queue's last send. The user's sign-out must
// not hang on a slow network; what has not landed by then is dropped as before.
export const SIGN_OUT_FLUSH_TIMEOUT_MS = 2000;

// Called by the sign-out itself, before the identity is torn down: the queue sends
// what it holds, marked as the sitting's end. Never throws, and waits at most
// SIGN_OUT_FLUSH_TIMEOUT_MS.
export const flushEventsBeforeSignOut = async (
  timeoutMs: number = SIGN_OUT_FLUSH_TIMEOUT_MS,
): Promise<void> => {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<void>((resolve) => {
    timer = setTimeout(resolve, timeoutMs);
  });
  try {
    await Promise.race([eventQueue.flushBeforeSignOut().catch(() => {}), timeout]);
  } finally {
    if (timer) clearTimeout(timer);
  }
};

// Both senders keep what the server has not accepted and resend it with whatever
// auth token is current. A sign-out without a page reload would otherwise let the
// account that signs in next send the previous account's events and play-time
// notes as its own. Returns the unsubscribe.
export const discardPendingEventsOnSignOut = (auth: {
  onSignOut: (listener: () => void) => () => void;
}): (() => void) =>
  auth.onSignOut(() => {
    eventQueue.discardPending();
    playTimeNoteFlushController.discardPending();
  });
